import { getListTableColumns } from '@openx/components-administration/lib/UsersList/tableColumns';
import type { UseGetUsersTableData } from '@openx/components-administration/lib/utils/context/UserHookTypes';
import type { Account } from '@openx/types/account';
import type { FiltersConfig } from '@openx/types/chipFilters';
import { sortMenuButtonItems } from '@openx/utils/lib/tableHelpers';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useUserDeleteModal } from './useUserDeleteModal';

export const useGetUsersTableData: UseGetUsersTableData = (accounts: Account[], searchPhrase?: string) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleDeleteUser = useUserDeleteModal({ shouldRedirect: true });

  const columns = useMemo(() => getListTableColumns(t, searchPhrase), [t, searchPhrase]);

  const filtersConfig: FiltersConfig = useMemo((): FiltersConfig => {
    return {
      account: {
        name: t('Organization'),
        options: accounts?.map(({ name }) => ({ displayName: name, value: name })),
      },
      role: {
        name: t('Role'),
        options: [
          {
            value: 'admin',
          },
          {
            value: 'member',
          },
        ],
      },
      status: {
        name: t('Status'),
        options: [
          {
            displayName: t('Active'),
            value: 'true',
          },
          {
            displayName: t('Inactive'),
            value: 'false',
          },
        ],
      },
    };
  }, [t, accounts]);

  const rowActions = useMemo(
    () =>
      sortMenuButtonItems({
        DETAILS: {
          'data-test': 'details-action-button',
          label: t('Details'),
          onClick: ({ id }) => {
            navigate(`/users/${id}/details`);
          },
        },
        EDIT: {
          'data-test': 'edit-action-button',
          label: t('Edit'),
          onClick: ({ id }) => {
            navigate(`/users/${id}/edit`);
          },
        },
        DELETE: {
          danger: true,
          'data-test': 'delete-action-button',
          label: t('Delete'),
          onClick: ({ id, name }) => {
            handleDeleteUser({ id, name });
          },
        },
      }),
    [t, handleDeleteUser, navigate]
  );

  return {
    columns,
    filtersConfig,
    rowActions,
  };
};
